import React, { useState, useEffect, useContext } from "react";
import styled from "styled-components";
import { FiPlus, FiTrash2, FiColumns, FiEdit2, FiInbox } from "react-icons/fi";
import { AppContext } from "../../../../AppContext";
import MobileSubNavigator from "../../../../components/MobileSubNavigator";
import MobileColumnEditModal from "../../../../components/Board/DynamicBoard/ManageBoard/MobileColumnEditModal";
import MobileBoardEditModal from "../../../../components/Board/DynamicBoard/ManageBoard/MobileBoardEditModal";
import { getHomeLinks } from "../../../../utils/homeLinks";
import {
  tokens,
  Button,
  Badge,
  Tag,
  SearchInput,
  EmptyState,
  Pagination,
} from "../../../../components/admin/AdminUI";

const c = tokens.color;

const Container = styled.div`
  width: 100%;
  max-width: 100vw;
  margin: 0 auto;
  padding: 16px 14px 40px;
  background: ${c.appBg};
  min-height: 100vh;
  box-sizing: border-box;
  overflow-x: hidden;
  font-family: ${tokens.font};
  color: ${c.text};
  -webkit-font-smoothing: antialiased;
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin-bottom: 16px;
  background: ${c.surface};
  padding: 18px 16px;
  border-radius: ${tokens.radius.lg};
  box-shadow: ${tokens.shadow.card};
  border: 1px solid ${c.border};
  box-sizing: border-box;
`;

const HeaderTitle = styled.h2`
  margin: 0;
  color: ${c.heading};
  font-size: 20px;
  font-weight: 700;
  letter-spacing: -0.02em;
`;

const HeaderSubtitle = styled.p`
  margin: 6px 0 0;
  color: ${c.muted};
  font-size: 13px;
  line-height: 1.5;
`;

const SearchContainer = styled.div`
  display: flex;
  gap: 8px;
  width: 100%;
  align-items: center;

  & > *:first-child {
    flex: 1 1 auto;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  width: 100%;
`;

const BoardCard = styled.div`
  background: ${c.surface};
  border: 1px solid ${c.border};
  border-radius: ${tokens.radius.lg};
  padding: 16px;
  margin-bottom: 12px;
  color: ${c.text};
  position: relative;
  width: 100%;
  box-sizing: border-box;
  box-shadow: ${tokens.shadow.card};
  transition: box-shadow 0.15s, border-color 0.15s;

  &:hover {
    border-color: #d0d5dd;
  }
`;

const BoardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-bottom: ${(props) => (props.expanded ? "12px" : "0")};
  cursor: pointer;
  padding-right: 28px;
  min-height: 44px;
`;

const BoardTitle = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: ${c.heading};
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ExpandIcon = styled.span`
  font-size: 11px;
  transition: transform 0.25s ease;
  transform: ${(props) => (props.expanded ? "rotate(180deg)" : "rotate(0)")};
  color: ${c.subtle};
`;

const BoardInfo = styled.div`
  margin-top: 4px;
  padding: 14px;
  background: ${c.surfaceMuted};
  border: 1px solid ${c.borderSubtle};
  border-radius: ${tokens.radius.md};
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px 14px;
  margin-bottom: ${(props) => (props.hasDescription ? "12px" : "0")};
`;

const InfoItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;

const InfoLabel = styled.span`
  color: ${c.muted};
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
`;

const BadgeValue = styled.span`
  background: ${c.surface};
  color: ${c.text};
  border: 1px solid ${c.border};
  padding: 2px 8px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const DescriptionText = styled.div`
  color: ${c.muted};
  font-size: 13px;
  line-height: 1.5;
  padding: 10px 12px;
  background: ${c.surface};
  border-radius: ${tokens.radius.sm};
  border-left: 3px solid ${c.primary};
`;

const CardActions = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  margin-top: 14px;
  padding-top: 14px;
  border-top: 1px solid ${c.borderSubtle};
`;

const CardCheckbox = styled.input`
  position: absolute;
  top: 18px;
  right: 16px;
  width: 18px;
  height: 18px;
  accent-color: ${c.primary};
  cursor: pointer;
`;

const StateMessage = styled.div`
  text-align: center;
  color: ${c.muted};
  padding: 40px 24px;
  background: ${c.surface};
  border: 1px solid ${c.border};
  border-radius: ${tokens.radius.lg};
  box-shadow: ${tokens.shadow.card};
  font-size: 14px;
  font-weight: 500;
`;

const PaginationWrap = styled.div`
  background: ${c.surface};
  border: 1px solid ${c.border};
  border-radius: ${tokens.radius.lg};
  box-shadow: ${tokens.shadow.card};
  margin-top: 4px;
`;

const MobileBoardAdmin = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [boards, setBoards] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPosts, setTotalPosts] = useState(0);
  const [loading, setLoading] = useState(false);
  const [columnModalOpen, setColumnModalOpen] = useState(false);
  const [selectedBoardId, setSelectedBoardId] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedBoardData, setSelectedBoardData] = useState(null);
  const [expandedBoards, setExpandedBoards] = useState([]); // 확장된 보드 추적
  const { isLoggedIn, user } = useContext(AppContext);

  const postsPerPage = 10;
  const totalPages = Math.ceil(totalPosts / postsPerPage);

  const getAllBoardData = async (page, search = "") => {
    setLoading(true);
    try {
      const response = await fetch(
        `/request/get_all_boards?page=${page}&limit=${postsPerPage}&search=${encodeURIComponent(
          search
        )}`,
        {
          credentials: "include",
          headers: {
            Accept: "application/json",
          },
          method: "GET",
        }
      );

      if (!response.ok) {
        throw new Error("Network response was not ok");
      }

      const data = await response.json();
      setBoards(data.boardData || []);
      setTotalPosts(data.total || 0);
    } catch (error) {
      console.error("Error fetching data:", error);
      setBoards([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isLoggedIn && user) {
      getAllBoardData(currentPage, searchTerm);
    }
  }, [currentPage, isLoggedIn, user]);

  const handleSearch = () => {
    setCurrentPage(1);
    getAllBoardData(1, searchTerm);
  };

  const handleSelectRow = (boardId) => {
    setSelectedRows((prev) => {
      if (prev.includes(boardId)) {
        return prev.filter((id) => id !== boardId);
      } else {
        return [...prev, boardId];
      }
    });
  };

  const handleDeleteBoards = async () => {
    if (selectedRows.length === 0) {
      alert("삭제할 게시판을 선택해주세요.");
      return;
    }

    if (!window.confirm("선택한 게시판을 삭제하시겠습니까?")) {
      return;
    }

    try {
      const response = await fetch("/request/delete_boards", {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ boardIds: selectedRows }),
      });

      if (response.ok) {
        alert("게시판이 삭제되었습니다.");
        setSelectedRows([]);
        getAllBoardData(currentPage, searchTerm);
      } else {
        alert("삭제에 실패했습니다.");
      }
    } catch (error) {
      console.error("Error deleting boards:", error);
      alert("삭제 중 오류가 발생했습니다.");
    }
  };

  const handleColumnEdit = (boardId) => {
    setSelectedBoardId(boardId);
    setColumnModalOpen(true);
  };

  const handleCloseColumnModal = () => {
    setColumnModalOpen(false);
    setSelectedBoardId(null);
  };

  const handleAddBoard = () => {
    setSelectedBoardData(null);
    setEditModalOpen(true);
  };

  const handleEditBoard = (board) => {
    setSelectedBoardData(board);
    setEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setEditModalOpen(false);
    setSelectedBoardData(null);
  };

  const handleRefreshBoards = () => {
    getAllBoardData(currentPage, searchTerm);
  };

  const toggleBoardExpansion = (boardId) => {
    setExpandedBoards((prev) => {
      if (prev.includes(boardId)) {
        return prev.filter((id) => id !== boardId);
      } else {
        return [...prev, boardId];
      }
    });
  };

  return (
    <MobileSubNavigator
      links={getHomeLinks()}
      sectionTitle="게시판 관리"
      backPath="/"
      showBackButton={true}
    >
      <Container>
        <Header>
          <div>
            <HeaderTitle>메뉴관리</HeaderTitle>
            <HeaderSubtitle>게시판/메뉴를 관리합니다.</HeaderSubtitle>
          </div>

          <SearchContainer>
            <SearchInput
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onSearch={handleSearch}
              placeholder="검색어를 입력하세요"
              grow="1 1 auto"
            />
            <Button $variant="primary" onClick={handleSearch}>
              검색
            </Button>
          </SearchContainer>

          <ActionButtons>
            <Button $variant="primary" onClick={handleAddBoard}>
              <FiPlus /> 추가
            </Button>
            <Button $variant="dangerSoft" onClick={handleDeleteBoards}>
              <FiTrash2 /> 삭제 ({selectedRows.length})
            </Button>
          </ActionButtons>
        </Header>

        {loading ? (
          <StateMessage>데이터를 불러오는 중...</StateMessage>
        ) : (
          <>
            {boards.length === 0 ? (
              <StateMessage>
                <EmptyState
                  icon={<FiInbox />}
                  title="게시판이 없습니다"
                  description="조건에 맞는 게시판이 없습니다. 검색어를 변경하거나 새 게시판을 추가해 보세요."
                  pad="8px"
                />
              </StateMessage>
            ) : (
              boards.map((board, index) => {
                const isExpanded = expandedBoards.includes(board.board_id);
                return (
                  <BoardCard key={board.board_id || index}>
                    <CardCheckbox
                      type="checkbox"
                      checked={selectedRows.includes(board.board_id)}
                      onChange={() => handleSelectRow(board.board_id)}
                    />

                    <BoardHeader
                      expanded={isExpanded}
                      onClick={() => toggleBoardExpansion(board.board_id)}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 10,
                          flexWrap: "wrap",
                          minWidth: 0,
                        }}
                      >
                        <BoardTitle>
                          {board.board_title}
                          <ExpandIcon expanded={isExpanded}>▼</ExpandIcon>
                        </BoardTitle>
                        {board.board_category && (
                          <Badge $tone="brand">{board.board_category}</Badge>
                        )}
                      </div>
                    </BoardHeader>

                    {isExpanded && (
                      <>
                        <BoardInfo>
                          <InfoGrid
                            hasDescription={!!board.board_description}
                          >
                            <InfoItem>
                              <InfoLabel>Site</InfoLabel>
                              <BadgeValue>{board.board_site_id}</BadgeValue>
                            </InfoItem>
                            <InfoItem>
                              <InfoLabel>순서</InfoLabel>
                              <BadgeValue>{board.board_idx}</BadgeValue>
                            </InfoItem>
                            <InfoItem>
                              <InfoLabel>읽기</InfoLabel>
                              <BadgeValue>{board.board_read_auth}</BadgeValue>
                            </InfoItem>
                            <InfoItem>
                              <InfoLabel>쓰기</InfoLabel>
                              <BadgeValue>{board.board_write_auth}</BadgeValue>
                            </InfoItem>
                          </InfoGrid>

                          {board.board_description && (
                            <DescriptionText>
                              {board.board_description}
                            </DescriptionText>
                          )}
                        </BoardInfo>

                        <CardActions>
                          <Button
                            $variant="secondary"
                            $size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleColumnEdit(board.board_id);
                            }}
                          >
                            <FiColumns /> 컬럼편집
                          </Button>
                          <Button
                            $variant="primary"
                            $size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditBoard(board);
                            }}
                          >
                            <FiEdit2 /> 수정
                          </Button>
                        </CardActions>
                      </>
                    )}
                  </BoardCard>
                );
              })
            )}

            {totalPages > 1 && (
              <PaginationWrap>
                <Pagination
                  page={currentPage}
                  totalPages={totalPages}
                  onChange={setCurrentPage}
                  totalItems={totalPosts}
                  pageSize={postsPerPage}
                />
              </PaginationWrap>
            )}
          </>
        )}
      </Container>

      <MobileColumnEditModal
        isOpen={columnModalOpen}
        onClose={handleCloseColumnModal}
        boardId={selectedBoardId}
      />

      <MobileBoardEditModal
        isOpen={editModalOpen}
        onClose={handleCloseEditModal}
        boardData={selectedBoardData}
        onRefresh={handleRefreshBoards}
      />
    </MobileSubNavigator>
  );
};

export default MobileBoardAdmin;
